"use client";

import { FormEventHandler, useCallback, useMemo, useState } from "react";
import {
  ShowWithComedian,
  getPrimaryShowComedian,
  processShowData,
} from "@/data/shows";
import { ShowsNearbyEvents, trackEvent } from "@/analytics";
import ShowsNearbyFilters, { FilterState } from "./filters/ShowsNearbyFilters";

import { PageMargin } from "../common";
import { PostalCodeData } from "@/data/zipcode";
import { ResponsiveShowRow } from "@/components/shows";
import ShowsNearbyChangeLocationModal from "./ShowsNearbyChangeLocationModal";
import { ShowsNearbyEmptyShows } from "@punchup/venues/client";
import ShowsNearbyHeader from "./ShowsNearbyHeader";
import ShowsNearbyInvalidLocation from "./ShowsNearbyInvalidLocation";
import ShowsNearbyNoLocation from "./ShowsNearbyNoLocation";
import ShowsNearbyZeroLineupToggle, {
  ZeroLineupExperimentControls,
} from "./ShowsNearbyZeroLineupToggle";
import { showRowComedian } from "./lineupPlaceholder";
import { Skeleton } from "@/shadcn/ui/skeleton";
import { UpdateLocationFormValues } from "./types";
import dayjs from "@punchup/utils/dayjs";
import { debounce } from "../common/utils";
import { isPlaceholderComedian } from "@/data/comedians";
import { isTixologiWidgetLink } from "@punchup/tixologi/widget-origin";
import useSession from "@/hooks/useSession";

// Utility functions

const isPlaceholderShow = (show: ShowWithComedian): boolean => {
  // Filter out any show where the main comedian is a placeholder
  // BUT keep legitimate showcase shows that have real comedians in show_comedians array
  const mainComedianName = getPrimaryShowComedian(show)?.display_name;
  if (mainComedianName && isPlaceholderComedian(mainComedianName)) {
    // If it's a showcase show with real comedians, keep it
    if (show.show_comedians && show.show_comedians.length > 0) {
      // Check if ANY of the show comedians are real (not placeholders)
      const hasRealComedians = show.show_comedians.some(
        (comedian) => !isPlaceholderComedian(comedian.display_name)
      );
      // Only filter out if ALL comedians are placeholders
      return !hasRealComedians;
    }
    // For single-comedian shows with placeholder comedian, filter them out
    return true;
  }

  return false;
};

const shouldSkipGrouping = (show: ShowWithComedian): boolean => {
  // Skip grouping for showcase shows (multiple comedians)
  // These should be rendered individually to show all comedians
  if (show.show_comedians && show.show_comedians.length > 1) {
    return true;
  }

  // Skip grouping for shows with placeholder comedians - render them individually
  // This prevents legitimate shows like "Rubee Tuesdays", "Chocolate Sundaes"
  // from being grouped together under "Lineup Coming Soon"
  const mainComedianName = getPrimaryShowComedian(show)?.display_name;
  if (mainComedianName && isPlaceholderComedian(mainComedianName)) {
    return true;
  }

  // Allow Ticketmaster shows to be grouped by comedian name + venue combination
  // The stable comedian ID creation in the API route handles this grouping
  return false;
};

// Helper function to format city with state from location data
const formatCityWithState = (
  location: PostalCodeData | null,
  fallbackCity?: string | null
): string => {
  if (location?.city && (location.state_abbreviation || location.state)) {
    const stateCode = location.state_abbreviation || location.state;
    return `${location.city}, ${stateCode}`;
  }
  return fallbackCity || "";
};

interface ShowsNearbyContentProps {
  shows: ShowWithComedian[] | null;
  fastCheckoutShows: ShowWithComedian[];
  supplementalShows: ShowWithComedian[];
  showNoLocationError: boolean;
  invalidZipcode: boolean;
  isUS: boolean;
  zipCode: string | null;
  city?: string | null;
  location: PostalCodeData | null;
  buyTicketsShowId: string;
  setBuyTicketsShowId: (id: string) => void;
  followedComedianIds: string[];
  countryCode: string | null;
  radius: number;
  detectedCountryCode: string | null;
  updateQueryParams: (
    params: Record<string, string>,
    replace?: boolean,
    paramsToRemove?: string[]
  ) => void;
  pathname: string;
  /** Present only for users inside the gated zero-lineup experiment. */
  zeroLineupExperiment?: ZeroLineupExperimentControls;
}

export default function ShowsNearbyContent({
  shows,
  supplementalShows,
  showNoLocationError,
  invalidZipcode,
  zipCode,
  city,
  location,
  buyTicketsShowId,
  setBuyTicketsShowId,
  followedComedianIds,
  countryCode,
  radius,
  detectedCountryCode,
  updateQueryParams,
  zeroLineupExperiment,
}: ShowsNearbyContentProps) {
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [formValues, setFormValues] = useState<UpdateLocationFormValues>({
    city: formatCityWithState(location, city),
    postalCode: zipCode || "",
    countryCode: countryCode ?? "",
    radius: radius || 25,
    searchType: zipCode ? "postalCode" : "city",
  });

  // Add filter state - only if the feature is enabled
  const [filterState, setFilterState] = useState<FilterState>({
    dateFilter: "all",
    comedianFilter: "all",
    checkoutFilter: "all",
  });

  const session = useSession();
  const isLoggedIn = !!session?.user;

  const handleLocationModalOpen = () => {
    // Initialize form with either location data or query parameters
    setFormValues({
      city: formatCityWithState(location, city),
      postalCode: zipCode || "",
      countryCode: countryCode ?? detectedCountryCode ?? "",
      radius: radius || 25,
      searchType: "city",
    });
    setIsLocationModalOpen(true);
  };

  // Function to update both city and postalCode in sync
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleLocationFieldsUpdate = useCallback(
    debounce(
      async (
        updatedValues: Partial<UpdateLocationFormValues>,
        newFormValues: UpdateLocationFormValues
      ) => {
        // If we're updating the city and we have a valid city value
        if (updatedValues.city && updatedValues.city.includes(",")) {
          try {
            // Get postal code for the selected city
            const cityParts = updatedValues.city
              .split(",")
              .map((part) => part.trim());
            const cityName = cityParts[0];
            const stateCode = cityParts.length > 1 ? cityParts[1] : null;

            // Only attempt to fetch postal code if we have enough data
            if (cityName && stateCode && newFormValues.countryCode) {
              // Instead of a dedicated API endpoint, use the city search API to find matching postal code
              const params = new URLSearchParams({
                query: cityName,
                countryCode: newFormValues.countryCode,
              });

              const response = await fetch(
                `/api/city-search?${params.toString()}`
              );
              if (response.ok) {
                const cities: Array<{
                  name: string;
                  state: string;
                  postal_code: string;
                }> = await response.json();
                // Find the city that matches our selection
                const matchedCity = cities.find(
                  (c) =>
                    c.name.toLowerCase() === cityName.toLowerCase() &&
                    c.state.toLowerCase().includes(stateCode.toLowerCase())
                );

                if (matchedCity && matchedCity.postal_code) {
                  // Update the postal code field with the retrieved value
                  setFormValues((prev) => ({
                    ...prev,
                    postalCode: matchedCity.postal_code,
                  }));
                }
              }
            }
          } catch (error) {
            console.error("Error syncing postal code with city:", error);
          }
        }

        // Similarly, if postal code is updated, update the city field
        if (updatedValues.postalCode && updatedValues.postalCode.length >= 3) {
          try {
            // Only attempt to fetch city if we have enough data
            if (newFormValues.countryCode) {
              const params = new URLSearchParams({
                postalCode: updatedValues.postalCode,
                countryCode: newFormValues.countryCode,
              });

              const response = await fetch(
                `/api/postal-to-city?${params.toString()}`
              );
              if (response.ok) {
                const data = await response.json();
                if (data.city) {
                  // Format the city name
                  const cityName = data.city.name;
                  const stateCode = data.city.state;
                  const fullCityName = `${cityName}, ${stateCode}`;

                  // Only update if we have valid data
                  if (cityName && stateCode) {
                    setFormValues((prev) => ({ ...prev, city: fullCityName }));
                  }
                }
              }
            }
          } catch (error) {
            console.error("Error syncing city with postal code:", error);
          }
        }
      },
      300
    ),
    [setFormValues]
  );

  const handleUpdateLocationSubmit: FormEventHandler<HTMLFormElement> = (
    event
  ) => {
    event.preventDefault();

    const { city, postalCode, countryCode, radius, searchType } = formValues;

    // Track the event with appropriate data
    if (searchType === "city") {
      trackEvent(ShowsNearbyEvents.UPDATED_ZIPCODE, {
        City: city,
        CountryCode: countryCode,
        Radius: radius,
        SearchType: "city",
      });

      updateQueryParams(
        {
          city,
          countryCode,
          radius: radius.toString(),
        },
        false,
        ["postalCode"]
      );
    } else {
      trackEvent(ShowsNearbyEvents.UPDATED_ZIPCODE, {
        PostalCode: postalCode,
        CountryCode: countryCode,
        Radius: radius,
        SearchType: "postalCode",
        ...(city && { City: city }), // Also track city if available
      });

      updateQueryParams(
        {
          postalCode,
          countryCode,
          radius: radius.toString(),
        },
        false,
        ["city"]
      );
    }

    setIsLocationModalOpen(false);
  };

  const handleSetBuyTicketsShowId: React.Dispatch<
    React.SetStateAction<string>
  > = (value) => {
    if (typeof value === "function") {
      const newValue = value(buyTicketsShowId);
      setBuyTicketsShowId(newValue);
    } else {
      setBuyTicketsShowId(value);
    }
  };

  // Helper function to apply filters to a single show
  const applyFiltersToShow = useCallback(
    (show: ShowWithComedian) => {
      // Filter out placeholder shows (shows with no real title AND placeholder comedians)
      if (isPlaceholderShow(show)) {
        return false;
      }

      // Check if this is a Ticketmaster show (starts with 'tm-')
      const isTicketmasterShow = show.id.startsWith("tm-");

      // Filter by followed comedians in the show_comedians array
      if (filterState.comedianFilter === "myComedians") {
        // For Ticketmaster shows, since they're external comedians, they won't match followedComedianIds
        // So TM shows should be filtered out when showing "myComedians"
        if (isTicketmasterShow) {
          return false;
        }

        const hasFollowedShowComedian =
          show.show_comedians?.some((comedian) =>
            followedComedianIds.includes(comedian.id)
          ) ?? false;

        // Show the show if any show comedian is followed
        if (!hasFollowedShowComedian) {
          return false;
        }
      }

      // Filter out all "other comedians" (not followed comedians)
      if (filterState.comedianFilter === "otherComedians") {
        // For Ticketmaster shows, they should be included in "otherComedians" since they're not followed
        if (isTicketmasterShow) {
          return true; // TM shows are "other comedians" by definition
        }

        const hasFollowedShowComedian =
          show.show_comedians?.some((comedian) =>
            followedComedianIds.includes(comedian.id)
          ) ?? false;

        // Hide the show if any show comedian is followed
        if (hasFollowedShowComedian) {
          return false;
        }
      }

      // Filter by Punchup verified venues (shows with Tixologi tickets)
      if (
        filterState.checkoutFilter === "punchupVenues" &&
        !isTixologiWidgetLink(show.ticket_link)
      ) {
        return false;
      }

      // Filter by "external checkout" i.e. all shows that don't have a Tixologi ticket link
      if (
        filterState.checkoutFilter === "externalCheckout" &&
        isTixologiWidgetLink(show.ticket_link)
      ) {
        return false;
      }

      // Filter by date
      if (filterState.dateFilter !== "all") {
        const showDate = dayjs(show.datetime);
        const today = dayjs();
        const dayOfWeek = showDate.day(); // 0 is Sunday, 6 is Saturday
        const month = showDate.month();

        if (filterState.dateFilter === "thisWeek") {
          const endOfWeek = today.endOf("week");
          return showDate.isBefore(endOfWeek);
        } else if (filterState.dateFilter === "thisWeekend") {
          // Weekend is Friday, Saturday, Sunday
          return (
            (dayOfWeek === 5 || dayOfWeek === 6 || dayOfWeek === 0) &&
            showDate.isBefore(today.endOf("week"))
          );
        } else if (filterState.dateFilter === "thisMonth") {
          // only return shows for the current month
          return month === today.month();
        }
      }

      return true;
    },
    [filterState, followedComedianIds]
  );

  // Filter platform shows based on current filter state
  const filteredShows = useMemo(() => {
    if (!shows) {
      return null;
    }

    return shows.filter(applyFiltersToShow);
  }, [shows, applyFiltersToShow]);

  // Filter supplemental shows based on current filter state
  const filteredSupplementalShows = useMemo(() => {
    if (!supplementalShows) {
      return [];
    }

    return supplementalShows.filter(applyFiltersToShow);
  }, [supplementalShows, applyFiltersToShow]);

  // Use either the filtered shows or all shows depending on whether filters are enabled
  const displayShows = useMemo(() => {
    return filteredShows;
  }, [filteredShows]);

  // Update fast checkout shows based on filtered shows
  const filteredFastCheckoutShows = useMemo(() => {
    if (!displayShows) return [];

    return displayShows.filter((show) =>
      isTixologiWidgetLink(show.ticket_link)
    );
  }, [displayShows]);

  // Group fast checkout shows by comedian (handling showcase shows properly)
  const groupedFastCheckoutShows = useMemo(() => {
    if (!filteredFastCheckoutShows || filteredFastCheckoutShows.length === 0) {
      return null;
    }

    const groupedByComedian = filteredFastCheckoutShows.reduce(
      (acc, show) => {
        // For showcase shows with multiple comedians, don't group them
        // They will be rendered individually as showcase shows
        if (show.show_comedians && show.show_comedians.length > 1) {
          // Skip grouping for showcase shows
          return acc;
        }

        // Skip grouping for shows that shouldn't be grouped
        if (shouldSkipGrouping(show)) {
          return acc;
        }

        const comedian = showRowComedian(show);
        if (!acc[comedian.id]) {
          acc[comedian.id] = {
            comedian,
            shows: [],
          };
        }
        acc[comedian.id].shows.push(show);

        return acc;
      },
      {} as Record<
        string,
        {
          comedian: NonNullable<ShowWithComedian["comedian"]>;
          shows: ShowWithComedian[];
        }
      >
    );

    // Sort comedians by nearest show date
    return Object.values(groupedByComedian).sort((a, b) => {
      const aEarliestDate = Math.min(
        ...a.shows.map((s) => dayjs(s.datetime).valueOf())
      );
      const bEarliestDate = Math.min(
        ...b.shows.map((s) => dayjs(s.datetime).valueOf())
      );
      return aEarliestDate - bEarliestDate;
    });
  }, [filteredFastCheckoutShows]);

  // Render function for fast checkout shows
  const renderGroupedFastCheckoutShows = () => {
    const result: JSX.Element[] = [];
    const processedShowIds = new Set<string>();

    // Create a combined list of all shows to render with their sort keys
    const showsToRender: Array<{ show: ShowWithComedian; sortKey: number }> =
      [];

    // Add showcase shows (not grouped)
    filteredFastCheckoutShows.forEach((show) => {
      if (show.show_comedians && show.show_comedians.length > 1) {
        showsToRender.push({
          show,
          sortKey: dayjs(show.datetime).valueOf(),
        });
        processedShowIds.add(show.id);
      }
    });

    // Add grouped single-comedian shows
    if (groupedFastCheckoutShows) {
      groupedFastCheckoutShows.forEach(({ comedian, shows }) => {
        // Get the earliest show date for sorting
        const earliestDate = Math.min(
          ...shows.map((s) => dayjs(s.datetime).valueOf())
        );

        // Mark all shows in this group as processed
        shows.forEach((s) => processedShowIds.add(s.id));

        // Add to render list with sort key
        showsToRender.push({
          show: shows[0], // Use first show as representative
          sortKey: earliestDate,
        });
      });
    }

    // Shows that skip grouping (placeholder-comedian shows) render individually
    filteredFastCheckoutShows.forEach((show) => {
      if (!processedShowIds.has(show.id)) {
        showsToRender.push({
          show,
          sortKey: dayjs(show.datetime).valueOf(),
        });
      }
    });

    // Sort all items by date
    showsToRender.sort((a, b) => a.sortKey - b.sortKey);

    // Render sorted items
    showsToRender.forEach(({ show }) => {
      // Check if this is part of a group
      const group = groupedFastCheckoutShows?.find((g) =>
        g.shows.some((s) => s.id === show.id)
      );

      if (group && group.shows.length > 1) {
        // Render as grouped shows
        const venueSlug = group.shows[0].venue_pages[0]?.slug || null;

        result.push(
          <ResponsiveShowRow
            key={`${group.comedian.id}-fast-checkout-group`}
            shows={group.shows.map((s) => processShowData(s))}
            showcaseShow={group.shows[0]}
            comedian={group.comedian}
            location={"shows-nearby"}
            isNearby={true}
            buyTicketsShowId={buyTicketsShowId}
            setBuyTicketsShowId={handleSetBuyTicketsShowId}
            isFollowed={followedComedianIds.includes(group.comedian.id)}
            followedComedianIds={followedComedianIds}
            venueSlug={venueSlug}
          />
        );
      } else {
        // Render as individual show (showcase or single)
        const row = renderShow(show);
        if (row) {
          result.push(row);
        }
      }
    });

    return result;
  };

  // Group shows by comedian (handling showcase shows properly)
  const groupedShows = useMemo(() => {
    if (!displayShows) {
      return null;
    }

    const groupedByComedian = displayShows.reduce(
      (acc, show) => {
        // For showcase shows with multiple comedians, don't group them
        // They will be rendered individually as showcase shows
        if (show.show_comedians && show.show_comedians.length > 1) {
          // Skip grouping for showcase shows
          return acc;
        }

        // Skip grouping for shows that shouldn't be grouped
        if (shouldSkipGrouping(show)) {
          return acc;
        }

        const comedian = showRowComedian(show);
        if (!acc[comedian.id]) {
          acc[comedian.id] = {
            comedian,
            shows: [],
          };
        }
        acc[comedian.id].shows.push(show);

        return acc;
      },
      {} as Record<
        string,
        {
          comedian: NonNullable<ShowWithComedian["comedian"]>;
          shows: ShowWithComedian[];
        }
      >
    );

    // Sort comedians by nearest show date
    return Object.values(groupedByComedian).sort((a, b) => {
      const aEarliestDate = Math.min(
        ...a.shows.map((s) => dayjs(s.datetime).valueOf())
      );
      const bEarliestDate = Math.min(
        ...b.shows.map((s) => dayjs(s.datetime).valueOf())
      );
      return aEarliestDate - bEarliestDate;
    });
  }, [displayShows]);

  const renderShow = (show: ShowWithComedian) => {
    const comedian = showRowComedian(show);

    // Get the venue slug for this show
    const venueSlug = show.venue_pages[0]?.slug || null;

    const showCaseShow =
      show.show_comedians && show.show_comedians.length > 1 ? show : undefined;

    return (
      <ResponsiveShowRow
        key={show.id}
        shows={[processShowData(show)]}
        showcaseShow={showCaseShow}
        comedian={comedian}
        location="shows-nearby"
        isNearby={showCaseShow ? true : false}
        buyTicketsShowId={buyTicketsShowId}
        setBuyTicketsShowId={handleSetBuyTicketsShowId}
        isFollowed={followedComedianIds.includes(comedian.id)}
        followedComedianIds={followedComedianIds}
        venueSlug={venueSlug}
      />
    );
  };

  const renderGroupedShows = () => {
    if (!displayShows) return null;

    const result: JSX.Element[] = [];
    const processedShowIds = new Set<string>();

    // Create a combined list of all shows to render with their sort keys
    const showsToRender: Array<{ show: ShowWithComedian; sortKey: number }> =
      [];

    // Add showcase shows (not grouped)
    displayShows.forEach((show) => {
      if (show.show_comedians && show.show_comedians.length > 1) {
        showsToRender.push({
          show,
          sortKey: dayjs(show.datetime).valueOf(),
        });
        processedShowIds.add(show.id);
      }
    });

    // Add grouped single-comedian shows
    if (groupedShows) {
      groupedShows.forEach(({ comedian, shows }) => {
        // Get the earliest show date for sorting
        const earliestDate = Math.min(
          ...shows.map((s) => dayjs(s.datetime).valueOf())
        );

        // Mark all shows in this group as processed
        shows.forEach((s) => processedShowIds.add(s.id));

        // Add to render list with sort key
        showsToRender.push({
          show: shows[0], // Use first show as representative
          sortKey: earliestDate,
        });
      });
    }

    // Add any remaining shows that weren't processed (shouldn't happen, but safety check)
    displayShows.forEach((show) => {
      if (!processedShowIds.has(show.id)) {
        showsToRender.push({
          show,
          sortKey: dayjs(show.datetime).valueOf(),
        });
      }
    });

    // Sort all items by date
    showsToRender.sort((a, b) => a.sortKey - b.sortKey);

    // Render sorted items
    showsToRender.forEach(({ show }) => {
      // Check if this is part of a group
      const group = groupedShows?.find((g) =>
        g.shows.some((s) => s.id === show.id)
      );

      if (group && group.shows.length > 1) {
        // Render as grouped shows
        const venueSlug = group.shows[0].venue_pages[0]?.slug || null;

        result.push(
          <ResponsiveShowRow
            key={`${group.comedian.id}-group`}
            shows={group.shows.map((s) => processShowData(s))}
            showcaseShow={group.shows[0]}
            comedian={group.comedian}
            location={"shows-nearby"}
            isNearby={true}
            buyTicketsShowId={buyTicketsShowId}
            setBuyTicketsShowId={handleSetBuyTicketsShowId}
            isFollowed={followedComedianIds.includes(group.comedian.id)}
            followedComedianIds={followedComedianIds}
            venueSlug={venueSlug}
          />
        );
      } else {
        // Render as individual show (showcase or single)
        const row = renderShow(show);
        if (row) {
          result.push(row);
        }
      }
    });

    return result;
  };

  // Render function for supplemental shows
  const renderSupplementalShows = () => {
    if (!supplementalShows || supplementalShows.length === 0) return null;

    return supplementalShows.map((show) => renderShow(show));
  };

  // Merge and sort all shows (platform + supplemental) by date while preserving grouping logic
  const renderConsolidatedShows = () => {
    if (!displayShows) return null;

    // Combine displayShows and filteredSupplementalShows into one array
    const allShows = [...displayShows, ...filteredSupplementalShows];

    // Create show rendering items using the same logic as renderGroupedShows but with all shows
    const result: JSX.Element[] = [];
    const processedShowIds = new Set<string>();

    // Create a combined list of all shows to render with their sort keys
    const showsToRender: Array<{ show: ShowWithComedian; sortKey: number }> =
      [];

    // Add showcase shows (not grouped) from all shows
    allShows.forEach((show) => {
      if (show.show_comedians && show.show_comedians.length > 1) {
        showsToRender.push({
          show,
          sortKey: dayjs(show.datetime).valueOf(),
        });
        processedShowIds.add(show.id);
      }
    });

    // Group single-comedian shows by comedian (from all shows)
    const groupedByComedian = allShows.reduce(
      (acc, show) => {
        // Skip if already processed or is showcase show
        if (
          processedShowIds.has(show.id) ||
          (show.show_comedians && show.show_comedians.length > 1) ||
          shouldSkipGrouping(show)
        ) {
          return acc;
        }

        const comedian = showRowComedian(show);
        if (!acc[comedian.id]) {
          acc[comedian.id] = {
            comedian,
            shows: [],
          };
        }
        acc[comedian.id].shows.push(show);

        return acc;
      },
      {} as Record<
        string,
        {
          comedian: NonNullable<ShowWithComedian["comedian"]>;
          shows: ShowWithComedian[];
        }
      >
    );

    // Add grouped shows
    Object.values(groupedByComedian).forEach(({ shows }) => {
      // Get the earliest show date for sorting
      const earliestDate = Math.min(
        ...shows.map((s) => dayjs(s.datetime).valueOf())
      );

      // Mark all shows in this group as processed
      shows.forEach((s) => processedShowIds.add(s.id));

      // Add to render list with sort key
      showsToRender.push({
        show: shows[0], // Use first show as representative
        sortKey: earliestDate,
      });
    });

    // Add any remaining shows that weren't processed (shouldn't happen, but safety check)
    allShows.forEach((show) => {
      if (!processedShowIds.has(show.id)) {
        showsToRender.push({
          show,
          sortKey: dayjs(show.datetime).valueOf(),
        });
      }
    });

    // Sort all items by date
    showsToRender.sort((a, b) => a.sortKey - b.sortKey);

    // Render sorted items
    showsToRender.forEach(({ show }) => {
      // Check if this is part of a group
      const group = Object.values(groupedByComedian).find((g) =>
        g.shows.some((s) => s.id === show.id)
      );

      if (group && group.shows.length > 1) {
        // Render as grouped shows
        const venueSlug = group.shows[0].venue_pages[0]?.slug || null;

        result.push(
          <ResponsiveShowRow
            key={`${group.comedian.id}-consolidated-group`}
            shows={group.shows.map((s) => processShowData(s))}
            showcaseShow={group.shows[0]}
            comedian={group.comedian}
            location={"shows-nearby"}
            isNearby={true}
            buyTicketsShowId={buyTicketsShowId}
            setBuyTicketsShowId={handleSetBuyTicketsShowId}
            isFollowed={followedComedianIds.includes(group.comedian.id)}
            followedComedianIds={followedComedianIds}
            venueSlug={venueSlug}
          />
        );
      } else {
        // Render as individual show (showcase or single)
        const row = renderShow(show);
        if (row) {
          result.push(row);
        }
      }
    });

    return result;
  };

  const getShowsTitle = (filterState: FilterState) => {
    // if we aren't filtering by comedian, check the date filter and return the appropriate title
    if (filterState.comedianFilter === "all") {
      if (filterState.dateFilter === "thisMonth") {
        const today = dayjs();
        const month = today.month();
        const monthName = today.month(month).format("MMMM");
        return `Shows in ${monthName}`;
      } else if (filterState.dateFilter === "thisWeek") {
        return "Shows this week";
      } else if (filterState.dateFilter === "thisWeekend") {
        return "Shows this weekend";
      }
    }

    // if we are filtering by comedian, return the appropriate title
    if (filterState.comedianFilter === "myComedians") {
      if (filterState.dateFilter === "thisMonth") {
        const today = dayjs();
        const month = today.month();
        const monthName = today.month(month).format("MMMM");
        return `Shows by Your Comedians in ${monthName}`;
      } else if (filterState.dateFilter === "thisWeek") {
        return "Shows by Your Comedians this week";
      } else if (filterState.dateFilter === "thisWeekend") {
        return "Shows by Your Comedians this weekend";
      } else {
        return "Your Comedians";
      }
    }

    // if we are filtering by other comedians, return the appropriate title
    if (filterState.comedianFilter === "otherComedians") {
      if (filterState.dateFilter === "thisMonth") {
        const today = dayjs();
        const month = today.month();
        const monthName = today.month(month).format("MMMM");
        return `Shows by Other Comedians in ${monthName}`;
      } else if (filterState.dateFilter === "thisWeek") {
        return "Shows by Other Comedians this week";
      } else if (filterState.dateFilter === "thisWeekend") {
        return "Shows by Other Comedians this weekend";
      } else {
        return "Other Comedians";
      }
    }

    // otherwise, default to "All Shows"
    return "All shows";
  };

  return (
    <>
      <PageMargin noBottomMargin>
        <ShowsNearbyHeader
          location={location}
          isShowingChangeLocation={invalidZipcode || showNoLocationError}
          countryCode={countryCode}
          handleLocationModalOpen={handleLocationModalOpen}
        />
      </PageMargin>

      <div className="mt-6">
        <ShowsNearbyFilters
          filterState={filterState}
          onFilterChange={setFilterState}
          followedComedianIds={followedComedianIds}
          isLoggedIn={isLoggedIn}
          isLoading={
            displayShows === null && !invalidZipcode && !showNoLocationError
          }
          radius={radius}
          openLocationModal={handleLocationModalOpen}
        />
      </div>

      {zeroLineupExperiment && (
        <PageMargin noBottomMargin>
          <div className="mt-4">
            <ShowsNearbyZeroLineupToggle {...zeroLineupExperiment} />
          </div>
        </PageMargin>
      )}

      {/* the modal for changing location */}
      <ShowsNearbyChangeLocationModal
        isOpen={isLocationModalOpen}
        setIsOpen={setIsLocationModalOpen}
        handleSubmit={handleUpdateLocationSubmit}
        handleUpdate={(updatedValues) => {
          // Update state immediately to show the user the updated values before we debounce the API call
          const newFormValues = { ...formValues, ...updatedValues };
          setFormValues(newFormValues);
          handleLocationFieldsUpdate(updatedValues, newFormValues);
        }}
        formValues={formValues}
        setFormValues={setFormValues}
        countryCode={countryCode}
        detectedCountryCode={detectedCountryCode}
      />

      {/* Content Section */}
      <div className="mt-6">
        <PageMargin>
          {showNoLocationError ? (
            <ShowsNearbyNoLocation />
          ) : invalidZipcode ? (
            <ShowsNearbyInvalidLocation />
          ) : displayShows ? (
            displayShows.length === 0 ? (
              <ShowsNearbyEmptyShows />
            ) : (
              <div className="flex flex-col gap-2 md:gap-8">
                {filteredFastCheckoutShows.length > 0 && (
                  <section>
                    <h2 className="text-xl font-semibold mb-4">
                      Fast Checkout
                    </h2>
                    <div className="flex flex-col gap-5 md:gap-4">
                      {renderGroupedFastCheckoutShows()}
                    </div>
                  </section>
                )}
                {/* Show consolidated view when filtered supplemental shows exist, otherwise show separate sections */}
                {filteredSupplementalShows.length > 0 ? (
                  <section>
                    <h2 className="text-xl font-semibold mb-4">
                      {getShowsTitle(filterState)}
                    </h2>
                    <div className="flex flex-col gap-5 md:gap-4">
                      {renderConsolidatedShows()}
                    </div>
                  </section>
                ) : (
                  <section>
                    <h2 className="text-xl font-semibold mb-4">
                      {getShowsTitle(filterState)}
                    </h2>
                    <div className="flex flex-col gap-5 md:gap-4">
                      {renderGroupedShows()}
                    </div>
                  </section>
                )}
              </div>
            )
          ) : (
            <ul className="flex flex-col gap-4">
              {Array.from({ length: 3 }).map((_, index) => (
                <li key={index}>
                  <Skeleton className="w-full h-[250px] md:h-[166px]" />
                </li>
              ))}
            </ul>
          )}
        </PageMargin>
      </div>
    </>
  );
}
